import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { logAudit } from '../hooks/useAudit';
import Dialog from './Dialog';
import { IconClock } from './dialogIcons';

export default function EditRequestModal({ count, profile, onClose, onSuccess, toast }) {
  const [form, setForm]         = useState({ reason: '', details: '', urgency: 'normal' });
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (!form.reason)        { toast.error('Please select a reason.'); return; }
    if (!form.details.trim()) { toast.error('Please provide details.'); return; }
    setSubmitting(true);
    /*
      **Saved with the rep's own id, and checked**, 18 September 2026 (the audit
      after the rebrand, issue 9). A rep reads only the tasks that carry their
      id, so without it their own request never appeared under "Raised by you",
      "Asked to reopen" vanished on reload and could be sent again and again,
      and a refused save still said the request had gone.
    */
    const { error } = await supabase.from('todos').insert({
      title:       'Edit request: ' + count.warehouse?.name,
      rep_id:      profile?.id || null,
      description: (profile?.full_name || 'A rep') + ' is requesting to reopen count for ' + count.warehouse?.name,
      priority:    form.urgency === 'urgent' ? 'high' : 'normal',
      todo_type:   'edit_request',
      warehouse_id: count.warehouse?.id,
      count_id:    count.id,
      is_complete: false,
      metadata:    JSON.stringify({
        rep_name:     profile?.full_name,
        rep_email:    profile?.email,
        location_name: count.warehouse?.name,
        region:        count.warehouse?.region?.name,
        reason:       form.reason,
        details:      form.details,
        urgency:      form.urgency,
        requested_at: new Date().toISOString(),
      }),
    });
    if (error) {
      setSubmitting(false);
      toast.error('That request was not sent, so nobody has been asked. Try again.');
      return;
    }
    await logAudit(profile, 'COUNT_EDIT_REQUESTED', 'count',
      { target_type: 'count', target_name: count.warehouse?.name || 'a location',
        details: { count_id: count.id, reason: form.reason } });
    onSuccess(count.id);
    onClose();
    toast.success('Your request has gone to an administrator, who decides.');
    setSubmitting(false);
  }

  const ready = !!form.reason && form.details.trim().length > 0;

  return (
    <Dialog
      kind="info" icon={<IconClock />}
      title="Ask to reopen this count"
      record={count.warehouse?.name}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-quiet" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={submitting || !ready}>
            {submitting ? 'Sending...' : 'Send the request'}
          </button>
        </>
      }
    >
      <div style={{ background: 'var(--warning-bg)', borderRadius: 12, padding: '12px 14px', fontSize: 13, color: 'var(--warning-ink)', lineHeight: 1.55 }}>
        This count is submitted and locked. An admin has to agree before you can change anything on it.
      </div>

      <div className="form-lbl">What went wrong</div>
      <select className="form-sel" value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))}>
        <option value="">Pick one</option>
        <option value="incorrect_quantity">Incorrect quantity</option>
        <option value="missing_items">Something is missing</option>
        <option value="wrong_item">The wrong item was added</option>
        <option value="data_entry_error">A number was typed wrongly</option>
        <option value="other">Something else</option>
      </select>

      <div className="form-lbl">How urgent</div>
      <div style={{ display: 'flex', gap: 8 }}>
        {[['normal', 'Normal'], ['urgent', 'Urgent']].map(([key, label]) => (
          <button key={key} type="button" onClick={() => setForm(p => ({ ...p, urgency: key }))}
            className={'btn ' + (form.urgency === key ? 'btn-primary' : 'btn-quiet')}
            style={{ flex: 1, minHeight: 44 }}>
            {label}
          </button>
        ))}
      </div>

      <div className="form-lbl">What needs correcting</div>
      <textarea className="form-ta" value={form.details}
        onChange={e => setForm(p => ({ ...p, details: e.target.value }))}
        placeholder="In your own words. The admin reads this." />
    </Dialog>
  );
}
